import { useCallback, useEffect, useState } from 'react';
import { wizardService, type WizardStatusResponse } from '@/services';
import { getErrorMessage } from '@/utils/error';

export interface WizardStatus {
  wizardCompleted: boolean;
  wizardStep: number;
  data: {
    productOrService?: 'Product' | 'Service';
    industry?: string;
    description?: string;
    serviceDescription?: string;
    keyPeople?: Array<{
      name: string;
      familyName?: string;
      linkedinUrl?: string;
      role: string;
      pictureUrl?: string;
    }>;
    brandColors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
    };
    logoUrl?: string;
    brandGuidelines?: string;
    competitors?: Array<{ id: number; name: string }>;
    linkedinUrl?: string;
    contentTypes?: Array<{ id: number; category: string }>;
    salesTarget?: {
      name?: string;
      industry?: string;
      role?: string;
    };
    pitchStrategy?: string;
    calendlyLink?: string;
    country?: string;
    countryCode?: string;
    timezone?: string;
    dailyPostCount?: number;
  };
}

export const useWizardStatus = () => {
  const [status, setStatus] = useState<WizardStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response: WizardStatusResponse = await wizardService.getStatus();
      setStatus(response);
      setError(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      console.error('Error fetching wizard status:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus(true);
  }, [fetchStatus]);

  // Silent refetch without loading state
  const silentRefetch = useCallback(async () => {
    await fetchStatus(false);
  }, [fetchStatus]);

  return { status, loading, error, refetch: fetchStatus, silentRefetch };
};

export const useWizardSubmit = () => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitStep = async (step: number, data: any) => {
    try {
      setSubmitting(true);
      setError(null);

      // Use wizard service based on step number
      switch (step) {
        case 1:
          await wizardService.saveStep1(data);
          break;
        case 2:
          await wizardService.saveStep2(data);
          break;
        case 3:
          await wizardService.saveStep3(data);
          break;
        case 4:
          await wizardService.saveStep4(data);
          break;
        case 5:
          await wizardService.saveStep5(data);
          break;
        case 6:
          await wizardService.saveStep6(data);
          break;
        case 7:
          await wizardService.saveStep7(data);
          break;
        case 8:
          await wizardService.saveStep8(data);
          break;
        default:
          throw new Error(`Invalid step number: ${step}`);
      }

      return { success: true };
    } catch (err: unknown) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      console.error(`Error saving step ${step}:`, err);
      return { success: false, error: errorMsg };
    } finally {
      setSubmitting(false);
    }
  };

  const completeWizard = async () => {
    try {
      setSubmitting(true);
      setError(null);
      await wizardService.complete();
      return { success: true };
    } catch (err: unknown) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      console.error('Error completing wizard:', err);
      return { success: false, error: errorMsg };
    } finally {
      setSubmitting(false);
    }
  };

  return { submitStep, completeWizard, submitting, error };
};

// Deprecated - use wizardService directly instead
export const useSearchIndustries = () => {
  const searchIndustries = async (query: string) => {
    try {
      return await wizardService.searchIndustries(query);
    } catch (err) {
      console.error('Error searching industries:', err);
      return [];
    }
  };

  return { searchIndustries };
};

// Deprecated - use wizardService directly instead
export const useSearchCompanies = () => {
  const searchCompanies = async (query: string) => {
    try {
      return await wizardService.searchCompanies(query);
    } catch (err) {
      console.error('Error searching companies:', err);
      return [];
    }
  };

  return { searchCompanies };
};
